import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Upload, FileSpreadsheet, Search, Download, ChevronDown, ChevronUp,
  AlertTriangle, Clock, CheckCircle, Loader2, X, Eye, BarChart3,
  ArrowUpDown, ChevronLeft, ChevronRight, Filter, FileDown, Trash2,
  Paperclip, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const STATUS_COLORS = {
  Open: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: '#ef4444' },
  'In Progress': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: '#f59e0b' },
  Closed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: '#10b981' },
};

const RISK_COLORS = { High: '#ef4444', Medium: '#f59e0b', Low: '#3b82f6', Critical: '#7c3aed', Info: '#6b7280' };
const PIE_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#6b7280'];
const PAGE_SIZE = 15;

export default function GapAssessmentPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isClient = user?.role === 'client' || user?.role === 'auditor';
  const canEditLimited = isAdmin || isClient; // client: only comments, owner, due_date
  const canManageEvidence = isAdmin || isClient;

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');

  // Sorting
  const [sortField, setSortField] = useState('sr_no');
  const [sortDir, setSortDir] = useState('asc');

  // Pagination
  const [page, setPage] = useState(1);

  // Modals
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const chartsRef = useRef(null);

  const vtq = () => (viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '');
  const vtp = () => (viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes, importsRes] = await Promise.all([
        axios.get(`${API}/gap-assessment${vtq()}`),
        axios.get(`${API}/gap-assessment/summary${vtq()}`),
        axios.get(`${API}/gap-assessment/imports${vtq()}`),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
      setImports(importsRes.data);
    } catch {
      toast.error('Failed to load gap assessment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [viewingOrg]);

  // ---- Upload ----
  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/gap-assessment/import${vtq()}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      toast.success(`Imported ${res.data.total_rows} items`);
      fetchAll();
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Import failed';
      toast.error(errMsg);
      setUploadResult({ error: true, message: errMsg });
    } finally {
      setUploading(false);
    }
  };

  // ---- Status change ----
  const handleStatusChange = async (itemId, newStatus) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, status: newStatus } : i)));
    try {
      await axios.patch(`${API}/gap-assessment/${itemId}${vtq()}`, { status: newStatus });
      const summaryRes = await axios.get(`${API}/gap-assessment/summary${vtq()}`);
      setSummary(summaryRes.data);
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
      fetchAll();
    }
  };

  // ---- Edit item (from detail modal) ----
  const handleSaveDetail = async () => {
    if (!editForm || !detailItem) return;
    try {
      await axios.patch(`${API}/gap-assessment/${detailItem.id}${vtq()}`, editForm);
      // Update local state
      setItems((prev) => prev.map((i) => (i.id === detailItem.id ? { ...i, ...editForm } : i)));
      setDetailItem((prev) => ({ ...prev, ...editForm }));
      const summaryRes = await axios.get(`${API}/gap-assessment/summary${vtq()}`);
      setSummary(summaryRes.data);
      toast.success('Changes saved');
    } catch {
      toast.error('Failed to save changes');
    }
  };

  // ---- Evidence upload / download / delete ----
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [deletingEvidenceId, setDeletingEvidenceId] = useState(null);

  const refreshDetailEvidence = async (itemId) => {
    try {
      const res = await axios.get(`${API}/gap-assessment/${itemId}/evidence${vtq()}`);
      const files = res.data || [];
      setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, evidence_files: files, evidence_uploaded: files.length > 0 } : i)));
      setDetailItem((prev) => (prev && prev.id === itemId ? { ...prev, evidence_files: files, evidence_uploaded: files.length > 0 } : prev));
    } catch {/* noop */}
  };

  const handleEvidenceUpload = async (item, inputEl) => {
    if (!canManageEvidence) {
      toast.error('You do not have permission to upload evidence');
      return;
    }
    const file = inputEl?.files?.[0];
    if (!file || !item) return;
    setUploadingEvidence(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/gap-assessment/${item.id}/evidence${vtq()}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded: ${file.name}`);
      inputEl.value = '';
      await refreshDetailEvidence(item.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleEvidenceDownload = async (documentId, fileName) => {
    try {
      const res = await axios.get(`${API}/gap-assessment/evidence/${documentId}/download${vtq()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `evidence-${documentId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download evidence');
    }
  };

  const handleEvidenceDelete = async (item, documentId) => {
    if (!canManageEvidence) return;
    if (!window.confirm('Delete this evidence file? It will also be removed from the Document Repository.')) return;
    setDeletingEvidenceId(documentId);
    try {
      await axios.delete(`${API}/gap-assessment/${item.id}/evidence/${documentId}${vtq()}`);
      toast.success('Evidence deleted');
      await refreshDetailEvidence(item.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete evidence');
    } finally {
      setDeletingEvidenceId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };


  // ---- Delete import ----
  const handleDeleteImport = async (importId, fileName) => {
    if (!window.confirm(`Delete import "${fileName}" and all its data?`)) return;
    try {
      await axios.delete(`${API}/gap-assessment/import/${importId}${vtq()}`);
      toast.success(`Deleted "${fileName}"`);
      fetchAll();
    } catch {
      toast.error('Failed to delete import');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete ALL gap assessment data for this tenant? This cannot be undone.')) return;
    try {
      for (const imp of imports) {
        await axios.delete(`${API}/gap-assessment/import/${imp.id}${vtq()}`);
      }
      toast.success('All gap assessment data cleared');
      fetchAll();
    } catch {
      toast.error('Failed to clear data');
    }
  };

  // ---- PDF Export ----
  const handleExportPdf = async () => {
    setExportingPdf(true);
    toast.info('Generating PDF...');
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');

      // Preload logo
      const logoImg = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          c.getContext('2d').drawImage(img, 0, 0);
          resolve(c.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = '/cyberforte-logo.png';
      });

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 14;
      const usable = pw - margin * 2;
      let y = margin;

      const tenantName = viewingOrg?.name || user?.tenant_name || 'Organization';
      const dateStr = new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });

      // Footer helper — draws logo + text at the bottom of each page
      const addFooter = () => {
        const footerY = ph - 16;
        // Divider line
        pdf.setDrawColor(203, 213, 225);
        pdf.line(margin, footerY - 3, pw - margin, footerY - 3);
        // Logo centered, larger
        if (logoImg) {
          const logoW = 38;
          const logoH = 14;
          pdf.addImage(logoImg, 'PNG', (pw - logoW) / 2, footerY - 1, logoW, logoH);
        }
        // Date left, text right
        pdf.setFontSize(7);
        pdf.setTextColor(148, 163, 184);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Generated on ${dateStr}`, margin, footerY + 7);
        pdf.text(`Cyber Forte Compliance Manager`, pw - margin, footerY + 7, { align: 'right' });
      };

      // ---- Page 1: Title + Summary + Charts ----
      // Title bar
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(0, 0, pw, 22, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(`Gap Assessment Report`, margin, 14);
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`${tenantName}  |  ${dateStr}`, pw - margin, 14, { align: 'right' });

      y = 30;
      pdf.setTextColor(30, 41, 59);

      // Summary cards
      if (summary) {
        const cards = [
          { label: 'Total Controls', value: summary.total, color: [30, 41, 59] },
          { label: 'Open Gaps', value: summary.status_breakdown.Open || 0, color: [239, 68, 68] },
          { label: 'In Progress', value: summary.status_breakdown['In Progress'] || 0, color: [245, 158, 11] },
          { label: 'Closed', value: summary.status_breakdown.Closed || 0, color: [16, 185, 129] },
        ];
        const cardW = (usable - 12) / 4;
        cards.forEach((c, i) => {
          const cx = margin + i * (cardW + 4);
          pdf.setDrawColor(200, 200, 200);
          pdf.setFillColor(249, 250, 251);
          pdf.roundedRect(cx, y, cardW, 20, 2, 2, 'FD');
          pdf.setFillColor(...c.color);
          pdf.rect(cx, y, 2, 20, 'F');
          pdf.setFontSize(8);
          pdf.setTextColor(100, 116, 139);
          pdf.text(c.label.toUpperCase(), cx + 6, y + 7);
          pdf.setFontSize(18);
          pdf.setTextColor(...c.color);
          pdf.setFont(undefined, 'bold');
          pdf.text(String(c.value), cx + 6, y + 16);
          pdf.setFont(undefined, 'normal');
        });
        y += 28;
      }

      // Charts capture
      if (chartsRef.current) {
        const canvas = await html2canvas(chartsRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        const imgW = usable;
        const imgH = (canvas.height / canvas.width) * imgW;
        const maxH = ph - y - margin - 15;
        const finalH = Math.min(imgH, maxH);
        pdf.addImage(imgData, 'PNG', margin, y, imgW, finalH);
        y += finalH + 6;
      }

      // ---- Data pages: full table ----
      const allData = [...filtered].sort((a, b) => (Number(a.sr_no) || 0) - (Number(b.sr_no) || 0));
      const colWidths = [10, 22, usable * 0.30, usable * 0.30, 20, 22, usable * 0.08];
      // Adjust so total = usable
      const totalCols = colWidths.reduce((s, w) => s + w, 0);
      const scale = usable / totalCols;
      const cols = colWidths.map(w => w * scale);
      const headers = ['#', 'Control', 'Issue', 'Recommendation', 'Status', 'Risk', 'Owner'];
      const rowHeight = 6;

      const addTableHeader = (yPos) => {
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPos, usable, rowHeight + 2, 'F');
        pdf.setFontSize(7);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(71, 85, 105);
        let x = margin + 2;
        headers.forEach((h, i) => {
          pdf.text(h.toUpperCase(), x, yPos + 4.5);
          x += cols[i];
        });
        pdf.setFont(undefined, 'normal');
        return yPos + rowHeight + 2;
      };

      const wrapText = (text, maxWidth, fontSize) => {
        pdf.setFontSize(fontSize);
        const words = (text || '-').replace(/\n/g, ' ').split(' ');
        const lines = [];
        let line = '';
        words.forEach(w => {
          const test = line ? `${line} ${w}` : w;
          if (pdf.getTextWidth(test) > maxWidth - 2) {
            if (line) lines.push(line);
            line = w;
          } else {
            line = test;
          }
        });
        if (line) lines.push(line);
        return lines.length ? lines : ['-'];
      };

      // Start new page for table
      pdf.addPage('landscape');
      // Title bar on table page
      pdf.setFillColor(30, 41, 59);
      pdf.rect(0, 0, pw, 14, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${tenantName} — Gap Assessment Details (${allData.length} items)`, margin, 9);
      pdf.setFont(undefined, 'normal');

      y = 18;
      y = addTableHeader(y);

      allData.forEach((item) => {
        const issueLines = wrapText(item.issue, cols[2], 7);
        const recLines = wrapText(item.recommendation, cols[3], 7);
        const maxLines = Math.max(issueLines.length, recLines.length, 1);
        const rh = Math.max(rowHeight, maxLines * 3.5 + 3);

        // Check page break (leave room for footer)
        if (y + rh > ph - margin - 16) {
          pdf.addPage('landscape');
          pdf.setFillColor(30, 41, 59);
          pdf.rect(0, 0, pw, 14, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.text(`${tenantName} — Gap Assessment Details (continued)`, margin, 9);
          pdf.setFont(undefined, 'normal');
          y = 18;
          y = addTableHeader(y);
        }

        // Alternating row bg
        if ((Number(item.sr_no) || 0) % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, y, usable, rh, 'F');
        }
        // Light row border
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, y + rh, margin + usable, y + rh);

        pdf.setFontSize(7);
        pdf.setTextColor(71, 85, 105);
        let x = margin + 2;

        // #
        pdf.text(String(item.sr_no || ''), x, y + 4);
        x += cols[0];

        // Control (stacked)
        const cids = item.control_id.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
        cids.forEach((cid, ci) => {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(30, 41, 59);
          pdf.text(cid, x, y + 4 + ci * 3.5);
        });
        pdf.setFont(undefined, 'normal');
        x += cols[1];

        // Issue
        pdf.setTextColor(51, 65, 85);
        issueLines.forEach((ln, li) => pdf.text(ln, x, y + 4 + li * 3.5));
        x += cols[2];

        // Recommendation
        pdf.setTextColor(71, 85, 105);
        recLines.forEach((ln, li) => pdf.text(ln, x, y + 4 + li * 3.5));
        x += cols[3];

        // Status badge
        const sc = { Open: [239, 68, 68], 'In Progress': [245, 158, 11], Closed: [16, 185, 129] };
        const statusColor = sc[item.status] || [100, 116, 139];
        pdf.setFillColor(...statusColor);
        pdf.roundedRect(x, y + 1.5, 18, 4.5, 1, 1, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(6);
        pdf.setFont(undefined, 'bold');
        pdf.text(item.status || '-', x + 1.5, y + 4.5);
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(7);
        x += cols[4];

        // Risk
        const rc = { High: [239, 68, 68], Medium: [245, 158, 11], Low: [59, 130, 246], Critical: [124, 58, 237] };
        pdf.setTextColor(...(rc[item.risk_rating] || [100, 116, 139]));
        pdf.setFont(undefined, 'bold');
        pdf.text(item.risk_rating || '-', x, y + 4);
        pdf.setFont(undefined, 'normal');
        x += cols[5];

        // Owner
        pdf.setTextColor(100, 116, 139);
        pdf.text((item.owner || '-').substring(0, 18), x, y + 4);

        y += rh;
      });

      // Footer on last page
      // Add footer with logo to ALL pages
      const totalPdfPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPdfPages; i++) {
        pdf.setPage(i);
        addFooter();
      }

      pdf.save(`Gap_Assessment_${tenantName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // ---- Filtering / sorting / pagination ----
  const filtered = useMemo(() => {
    let d = [...items];
    if (search) {
      const q = search.toLowerCase();
      d = d.filter(
        (i) =>
          i.control_id?.toLowerCase().includes(q) ||
          i.issue?.toLowerCase().includes(q) ||
          i.recommendation?.toLowerCase().includes(q) ||
          i.comments?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') d = d.filter((i) => i.status === statusFilter);
    if (riskFilter !== 'all') d = d.filter((i) => i.risk_rating === riskFilter);
    if (ownerFilter !== 'all') d = d.filter((i) => (i.owner || '') === ownerFilter);
    d.sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'sr_no') { va = Number(va) || 0; vb = Number(vb) || 0; }
      else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return d;
  }, [items, search, statusFilter, riskFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, statusFilter, riskFilter, ownerFilter]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  // ---- Unique owners for filter ----
  const uniqueOwners = useMemo(() => {
    const owners = [...new Set(items.map((i) => i.owner || '').filter(Boolean))].sort();
    return owners;
  }, [items]);

  // ---- Chart data ----
  const statusChartData = summary
    ? Object.entries(summary.status_breakdown).map(([name, value]) => ({ name, value }))
    : [];
  const riskChartData = summary
    ? Object.entries(summary.risk_breakdown)
        .filter(([k]) => k !== 'Unknown' && k)
        .map(([name, value]) => ({ name, value }))
    : [];

  const SortHeader = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-slate-900' : 'text-slate-300'}`} />
      </span>
    </th>
  );

  const StatusBadge = ({ status }) => {
    const c = STATUS_COLORS[status] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.bg} ${c.text} ${c.border}`} data-testid={`status-badge-${status}`}>
        {status === 'Open' && <AlertTriangle className="w-3 h-3" />}
        {status === 'In Progress' && <Clock className="w-3 h-3" />}
        {status === 'Closed' && <CheckCircle className="w-3 h-3" />}
        {status}
      </span>
    );
  };

  const RiskBadge = ({ risk }) => {
    const colors = {
      High: 'bg-red-100 text-red-800 border-red-200',
      Medium: 'bg-amber-100 text-amber-800 border-amber-200',
      Low: 'bg-blue-100 text-blue-800 border-blue-200',
      Critical: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return risk ? (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${colors[risk] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
        {risk}
      </span>
    ) : <span className="text-slate-400 text-xs">-</span>;
  };

  const TruncatedText = ({ text, max = 80, className = '' }) => {
    if (!text) return <span className="text-slate-400 text-xs">-</span>;
    if (text.length <= max) return <span className={className}>{text}</span>;
    return (
      <span className={className} title={text}>
        {text.slice(0, max)}...
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="gap-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gap-assessment-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-slate-700" />
            Gap Assessment
          </h1>
          <p className="text-slate-600 mt-1">
            {items.length > 0
              ? `${items.length} controls imported from gap assessment`
              : 'Upload an Excel gap assessment file to get started'}
          </p>
        </div>
        <div className="flex gap-2">
          {items.length > 0 && (
            <>
              <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf} data-testid="export-gap-pdf">
                {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
              <Button variant="outline" onClick={() => {
              const csv = [
                ['Sr No', 'Control ID', 'Issue', 'Recommendation', 'Control Explained', 'Remediation Type', 'Risk Rating', 'Status', 'Owner', 'Comments', 'CyberForte Comments', 'Due Date', 'Date Completed'].join(','),
                ...filtered.map(i => [
                  i.sr_no, i.control_id,
                  `"${(i.issue||'').replace(/"/g,'""').replace(/\n/g,' ')}"`,
                  `"${(i.recommendation||'').replace(/"/g,'""').replace(/\n/g,' ')}"`,
                  `"${(i.iso_control_explained||'').replace(/"/g,'""').replace(/\n/g,' ')}"`,
                  i.remediation_type, i.risk_rating, i.status, i.owner,
                  `"${(i.comments||'').replace(/"/g,'""').replace(/\n/g,' ')}"`,
                  `"${(i.cyberforte_comments||'').replace(/"/g,'""').replace(/\n/g,' ')}"`,
                  i.due_date||'', i.date_completed||''
                ].join(','))
              ].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `gap_assessment_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              toast.success('Exported');
            }} data-testid="export-gap-csv">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
            </>
          )}
          {(isAdmin || isClient) && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/gap-assessment/sync-from-iso${vtq()}`);
                  toast.success(res.data.message || 'Synced from ISO 27001');
                  fetchAll();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Sync failed');
                }
              }}
              data-testid="sync-from-iso-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Sync from ISO 27001
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => { setUploadOpen(true); setFile(null); setUploadResult(null); }} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="import-gap-btn">
              <Upload className="w-4 h-4 mr-2" />Import Gap Assessment
            </Button>
          )}
        </div>
      </div>

      {/* Summary Dashboard */}
      {summary && summary.total > 0 && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-slate-800">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Controls</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-controls">{summary.total}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Open Gaps</p>
                <p className="text-3xl font-bold text-red-600 mt-1" data-testid="open-gaps">{summary.status_breakdown.Open || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">In Progress</p>
                <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="in-progress-gaps">{summary.status_breakdown['In Progress'] || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Closed</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="closed-gaps">{summary.status_breakdown.Closed || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Donut */}
            <Card>
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Status Distribution</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {statusChartData.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.name]?.dot || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Risk Bar */}
            <Card>
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Risk Rating Breakdown</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={riskChartData} layout="vertical" margin={{ left: 10 }}>
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {riskChartData.map((entry, i) => (
                          <Cell key={i} fill={RISK_COLORS[entry.name] || '#6b7280'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Filters & Search */}
      {items.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by Control ID, issue, recommendation, or comments..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-white border-slate-300"
                  data-testid="gap-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-sm border border-slate-300 bg-white text-slate-700 px-3 py-2 text-sm"
                data-testid="gap-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Closed">Closed</option>
              </select>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="rounded-sm border border-slate-300 bg-white text-slate-700 px-3 py-2 text-sm"
                data-testid="gap-risk-filter"
              >
                <option value="all">All Risk Levels</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              {uniqueOwners.length > 0 && (
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="rounded-sm border border-slate-300 bg-white text-slate-700 px-3 py-2 text-sm"
                  data-testid="gap-owner-filter"
                >
                  <option value="all">All Owners</option>
                  {uniqueOwners.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">{filtered.length} of {items.length} items shown</p>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      {items.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="gap-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortHeader field="sr_no">#</SortHeader>
                  <SortHeader field="control_id">Control</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[35%]">Issue</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[35%]">Recommendation</th>
                  <SortHeader field="status">Status</SortHeader>
                  <SortHeader field="owner">Owner</SortHeader>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`gap-row-${item.control_id}`}>
                    <td className="px-4 py-3 text-sm text-slate-500 font-mono">{item.sr_no}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {item.control_id.split(/[,\n]+/).map((id) => id.trim()).filter(Boolean).map((id, idx) => (
                          <span key={idx} className="text-sm font-semibold text-slate-800 bg-slate-100 px-2 py-0.5 rounded font-mono w-fit">{id}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700 leading-snug line-clamp-3">{item.issue || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-600 leading-snug line-clamp-3">{item.recommendation || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin ? (
                        <select
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          className={`text-xs font-semibold rounded-full px-2.5 py-1 border cursor-pointer appearance-none text-center ${STATUS_COLORS[item.status]?.bg || 'bg-slate-50'} ${STATUS_COLORS[item.status]?.text || 'text-slate-600'} ${STATUS_COLORS[item.status]?.border || 'border-slate-200'}`}
                          data-testid={`status-select-${item.control_id}`}
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Closed">Closed</option>
                        </select>
                      ) : (
                        <StatusBadge status={item.status} />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600 whitespace-nowrap">{item.owner || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => { setDetailItem(item); setEditForm({}); }}
                        className="text-slate-500 hover:text-slate-800 transition-colors"
                        title="View full details"
                        data-testid={`view-detail-${item.control_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </Card>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No Gap Assessment Data</h3>
            <p className="text-slate-500 mt-1 mb-6">Upload an Excel gap assessment file to populate the dashboard</p>
            {isAdmin && (
              <Button onClick={() => { setUploadOpen(true); setFile(null); setUploadResult(null); }} className="bg-slate-900 text-white hover:bg-slate-800">
                <Upload className="w-4 h-4 mr-2" />Import Gap Assessment
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import History */}
      {imports.length > 0 && (
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Import History</h3>
              {isAdmin && imports.length > 1 && (
                <Button variant="outline" size="sm" onClick={handleClearAll} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="clear-all-imports">
                  <Trash2 className="w-3 h-3 mr-1" />Clear All
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {imports.map((imp) => (
                <div key={imp.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-sm" data-testid={`import-record-${imp.id}`}>
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-800">{imp.file_name}</span>
                    <span className="text-slate-500">{imp.total_rows} rows</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-xs">{imp.imported_by}</span>
                    <span className="text-slate-500 text-xs">{new Date(imp.created_at).toLocaleDateString()}</span>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteImport(imp.id, imp.file_name)}
                        className="text-red-400 hover:text-red-600 transition-colors ml-1"
                        title="Delete this import and its data"
                        data-testid={`delete-import-${imp.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-600" />
              Import Gap Assessment
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file (.xlsx) with gap assessment data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <p className="font-medium text-slate-800 mb-1">Expected columns:</p>
              <p className="text-slate-600 text-xs leading-relaxed">
                Control, Issue, Recommendation, Control Explained, Remediation Type, Risk Rating, Status, Owner, Comments, Cyber Forte Comments, Due Date, Date Completed
              </p>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f?.name.endsWith('.xlsx')) { setFile(f); setUploadResult(null); }
                else toast.error('Only .xlsx files accepted');
              }}
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-900">{file.name}</p>
                  <p className="text-xs text-emerald-600">{(file.size / 1024).toFixed(1)} KB</p>
                  <button onClick={() => setFile(null)} className="text-xs text-red-600 hover:text-red-800 underline mt-1">Remove</button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-slate-400" />
                  <p className="text-sm text-slate-600">Drag & drop your Excel file, or</p>
                  <label className="cursor-pointer">
                    <span className="text-sm text-blue-600 hover:text-blue-800 underline">browse to select</span>
                    <input type="file" accept=".xlsx" onChange={(e) => { if (e.target.files[0]) { setFile(e.target.files[0]); setUploadResult(null); } }} className="hidden" />
                  </label>
                </div>
              )}
            </div>

            {uploadResult && !uploadResult.error && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-emerald-900 text-sm">Imported {uploadResult.total_rows} items</span>
                </div>
                {uploadResult.columns_mapped && (
                  <p className="text-xs text-emerald-700 mb-2">
                    Mapped columns: {uploadResult.columns_mapped.join(', ')}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(uploadResult.status_breakdown || {}).map(([k, v]) => (
                    <div key={k} className="bg-white rounded p-2 border">
                      <span className="text-slate-500">{k}:</span> <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                  {Object.entries(uploadResult.risk_breakdown || {}).map(([k, v]) => (
                    <div key={k} className="bg-white rounded p-2 border">
                      <span className="text-slate-500">{k}:</span> <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadResult?.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <p className="text-sm text-red-800">{uploadResult.message}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>
              {uploadResult && !uploadResult.error ? 'Close' : 'Cancel'}
            </Button>
            {(!uploadResult || uploadResult.error) && (
              <Button onClick={handleUpload} disabled={!file || uploading} className="bg-slate-900 text-white hover:bg-slate-800">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Upload className="w-4 h-4 mr-2" />Import</>}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail / Edit Modal */}
      <Dialog open={!!detailItem} onOpenChange={(open) => { if (!open) setDetailItem(null); }}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          {detailItem && (() => {
            const editing = isAdmin;
            const auditorEdit = isClient; // limited-field edit mode
            const anyEdit = editing || auditorEdit;
            const f = editForm;
            const set = (field, val) => setEditForm((p) => ({ ...p, [field]: val }));
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="font-mono bg-slate-100 px-2 py-1 rounded text-sm">{detailItem.control_id}</span>
                    {editing ? (
                      <select value={f.status ?? detailItem.status} onChange={(e) => set('status', e.target.value)}
                        className={`text-xs font-semibold rounded-full px-2.5 py-1 border cursor-pointer ${STATUS_COLORS[f.status ?? detailItem.status]?.bg || 'bg-slate-50'} ${STATUS_COLORS[f.status ?? detailItem.status]?.text || 'text-slate-600'} ${STATUS_COLORS[f.status ?? detailItem.status]?.border || 'border-slate-200'}`}
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Closed">Closed</option>
                      </select>
                    ) : (
                      <StatusBadge status={detailItem.status} />
                    )}
                    {editing ? (
                      <select value={f.risk_rating ?? detailItem.risk_rating} onChange={(e) => set('risk_rating', e.target.value)}
                        className="text-xs font-medium rounded border border-slate-200 px-2 py-1"
                      >
                        <option value="">-</option>
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    ) : (
                      <RiskBadge risk={detailItem.risk_rating} />
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 py-2">
                  {/* Issue */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Issue</h4>
                    {editing ? (
                      <textarea value={f.issue ?? detailItem.issue ?? ''} onChange={(e) => set('issue', e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[80px] focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-issue" />
                    ) : (
                      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="detail-issue">{detailItem.issue || '-'}</p>
                    )}
                  </div>

                  {/* Recommendation */}
                  <div className={editing ? '' : 'bg-amber-50 border border-amber-200 rounded-lg p-4'}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wide mb-1 ${editing ? 'text-slate-500' : 'text-amber-700'}`}>Recommendation</h4>
                    {editing ? (
                      <textarea value={f.recommendation ?? detailItem.recommendation ?? ''} onChange={(e) => set('recommendation', e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[80px] focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-recommendation" />
                    ) : (
                      <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed" data-testid="detail-recommendation">{detailItem.recommendation || '-'}</p>
                    )}
                  </div>

                  {/* ISO Control Explained */}
                  {(detailItem.iso_control_explained || editing) && (
                    <div className={editing ? '' : 'bg-blue-50 border border-blue-200 rounded-lg p-4'}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wide mb-1 ${editing ? 'text-slate-500' : 'text-blue-700'}`}>Control Explained</h4>
                      {editing ? (
                        <textarea value={f.iso_control_explained ?? detailItem.iso_control_explained ?? ''} onChange={(e) => set('iso_control_explained', e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[80px] focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-iso-explained" />
                      ) : (
                        <p className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed" data-testid="detail-iso-explained">{detailItem.iso_control_explained}</p>
                      )}
                    </div>
                  )}

                  {/* Comments */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client Comments</h4>
                    {(editing || auditorEdit) ? (
                      <textarea value={f.comments ?? detailItem.comments ?? ''} onChange={(e) => set('comments', e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[60px] focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-comments" />
                    ) : (
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-lg p-3 border" data-testid="detail-comments">{detailItem.comments || '-'}</p>
                    )}
                  </div>

                  {/* CyberForte Comments */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Cyber Forte Comments</h4>
                    {editing ? (
                      <textarea value={f.cyberforte_comments ?? detailItem.cyberforte_comments ?? ''} onChange={(e) => set('cyberforte_comments', e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[60px] focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-cf-comments" />
                    ) : (
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-lg p-3 border" data-testid="detail-cf-comments">{detailItem.cyberforte_comments || '-'}</p>
                    )}
                  </div>

                  {/* Metadata row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Remediation Type</p>
                      {editing ? (
                        <input value={f.remediation_type ?? detailItem.remediation_type ?? ''} onChange={(e) => set('remediation_type', e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-remediation" />
                      ) : (
                        <p className="text-sm font-medium text-slate-800">{detailItem.remediation_type || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Owner</p>
                      {(editing || auditorEdit) ? (
                        <input value={f.owner ?? detailItem.owner ?? ''} onChange={(e) => set('owner', e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-owner" />
                      ) : (
                        <p className="text-sm font-medium text-slate-800">{detailItem.owner || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Due Date</p>
                      {(editing || auditorEdit) ? (
                        <input type="date" value={f.due_date ?? (detailItem.due_date ? detailItem.due_date.split('T')[0] : '')} onChange={(e) => set('due_date', e.target.value || null)}
                          className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-due-date" />
                      ) : (
                        <p className="text-sm font-medium text-slate-800">{detailItem.due_date ? new Date(detailItem.due_date).toLocaleDateString() : '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Date Completed</p>
                      {editing ? (
                        <input type="date" value={f.date_completed ?? (detailItem.date_completed ? detailItem.date_completed.split('T')[0] : '')} onChange={(e) => set('date_completed', e.target.value || null)}
                          className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-slate-400 focus:outline-none" data-testid="edit-date-completed" />
                      ) : (
                        <p className="text-sm font-medium text-slate-800">{detailItem.date_completed ? new Date(detailItem.date_completed).toLocaleDateString() : '-'}</p>
                      )}
                    </div>
                  </div>

                  {/* Evidence Files */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                        <Paperclip className="w-3.5 h-3.5" />
                        Evidence Files
                        {detailItem.evidence_files?.length > 0 && (
                          <span className="text-xs font-normal text-slate-500 normal-case">({detailItem.evidence_files.length})</span>
                        )}
                      </h4>
                      {canManageEvidence && (
                        <label
                          htmlFor={`gap-evidence-upload-${detailItem.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-slate-700"
                          data-testid="gap-upload-evidence-label"
                        >
                          {uploadingEvidence ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /> Upload Evidence</>
                          )}
                          <input
                            id={`gap-evidence-upload-${detailItem.id}`}
                            type="file"
                            className="hidden"
                            data-testid="gap-upload-evidence-input"
                            onChange={(e) => handleEvidenceUpload(detailItem, e.target)}
                            disabled={uploadingEvidence}
                          />
                        </label>
                      )}
                    </div>
                    {(detailItem.evidence_files && detailItem.evidence_files.length > 0) ? (
                      <ul className="space-y-1.5">
                        {detailItem.evidence_files.map((ef) => (
                          <li
                            key={ef.document_id}
                            className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-slate-800 truncate">{ef.file_name}</p>
                                <p className="text-xs text-slate-500">
                                  {formatFileSize(ef.file_size)} · {ef.uploaded_by}
                                  {ef.uploaded_at && ` · ${new Date(ef.uploaded_at).toLocaleDateString()}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleEvidenceDownload(ef.document_id, ef.file_name)}
                                className="p-1.5 rounded hover:bg-white text-slate-600 hover:text-slate-900"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {canManageEvidence && (
                                <button
                                  type="button"
                                  onClick={() => handleEvidenceDelete(detailItem, ef.document_id)}
                                  disabled={deletingEvidenceId === ef.document_id}
                                  className="p-1.5 rounded hover:bg-red-50 text-red-600 hover:text-red-700 disabled:opacity-40"
                                  title="Delete"
                                >
                                  {deletingEvidenceId === ef.document_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-500 italic">
                        {canManageEvidence ? 'No evidence uploaded yet. Use the Upload Evidence button above.' : 'No evidence uploaded yet.'}
                      </p>
                    )}
                    {canManageEvidence && (
                      <p className="text-[11px] text-slate-400 mt-2">
                        Files also appear in the Document Repository. To modify a file, delete it and upload the new version.
                      </p>
                    )}
                  </div>

                  {/* Save button for admins and auditors (auditor can save limited fields) */}
                  {(editing || auditorEdit) && (
                    <div className="flex justify-end pt-2 border-t">
                      <Button onClick={handleSaveDetail} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-detail-btn">
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
